import type { ColumnFilterItem, FilterValue } from 'antd/es/table/interface';
import {
	PROGRAMMING_STATUS_FILTERS,
	type ProgrammingStatus,
} from './programmingStatus';

/**
 * The grid's Progress column tracks three gates, and each one is Pending, In
 * progress or Done. They share a single column, so they share a single filter
 * menu: one submenu per gate, `gate:status` as the value, and the gate's own
 * query param on the way out.
 */
export const PROGRESS_GATES = [
	{ key: 'prog', label: 'Programming', field: 'programming_status' },
	{ key: 'first', label: 'First review', field: 'first_review_status' },
	{
		key: 'second',
		label: 'Secondary review',
		field: 'secondary_review_status',
	},
] as const;

export type ProgressGateField = (typeof PROGRESS_GATES)[number]['field'];

/** Statuses selected per gate; an absent gate is unfiltered. */
export type ProgressGateStatuses = Partial<
	Record<ProgressGateField, ProgrammingStatus[]>
>;

export const PROGRESS_FILTERS: ColumnFilterItem[] = PROGRESS_GATES.map(
	({ key, label }) => ({
		text: label,
		value: key,
		children: PROGRAMMING_STATUS_FILTERS.map(({ text, value }) => ({
			text,
			value: `${key}:${value}`,
		})),
	})
);

export const parseProgrammingStatusParam = (
	value: string | null
): ProgrammingStatus | undefined => {
	if (value === 'done' || value === 'in_progress' || value === 'pending') {
		return value;
	}
	return undefined;
};

/**
 * Gates are multi-select, so a param carries a comma-joined list. Unknown
 * entries are dropped rather than widening the result.
 */
export const parseProgrammingStatusesParam = (
	value: string | null
): ProgrammingStatus[] | undefined => {
	const statuses = (value ?? '')
		.split(',')
		.map((entry) => parseProgrammingStatusParam(entry.trim()))
		.filter((status): status is ProgrammingStatus => status !== undefined);
	return statuses.length ? statuses : undefined;
};

/** The checked keys antd should show for the current query. */
export const progressFilterValues = (gates: ProgressGateStatuses) => {
	const values = PROGRESS_GATES.flatMap(({ key, field }) =>
		(gates[field] ?? []).map((status) => `${key}:${status}`)
	);
	return values.length ? values : null;
};

/** The inverse: antd's checked keys back into per-gate statuses. */
export const parseProgressFilterValues = (
	value: FilterValue | null | undefined
): ProgressGateStatuses => {
	const parsed: ProgressGateStatuses = {};

	value?.forEach((entry) => {
		const [gateKey, rawStatus] = String(entry).split(':');
		const gate = PROGRESS_GATES.find(({ key }) => key === gateKey);
		const status = parseProgrammingStatusParam(rawStatus ?? null);
		if (gate && status) {
			parsed[gate.field] = [...(parsed[gate.field] ?? []), status];
		}
	});

	return parsed;
};
